import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { showError } from "@infrastructure/api/utils/toastNotifications";
import { fetchActivityEntryPrefillAsync } from "@features/report/activity-entry/services/activityEntrySvc";
import type { ActivityEntryScope } from "@features/report/activity-entry/hooks/useActivityEntryAction";
import type {
    ActivityEntryPrefillCandidate,
    ActivityEntryPrefillValuesResponse,
} from "@features/report/activity-entry/types/activityEntry";
import type { ActivityKpiContext } from "@features/report/activity-entry/utils/activityEntryGroupHelpers";
import {
    formatPrefillCandidateLabel,
    mapPrefillEntriesToFormValues,
    pickDefaultPrefillCandidate,
} from "@features/report/activity-entry/utils/activityEntryPrefill";

type ActivityEntryPrefillControlsProps = {
    scope: ActivityEntryScope;
    entityId: number;
    reportTemplateId: number;
    reportTagId: number;
    targetSince: string | null | undefined;
    kpiContexts: ActivityKpiContext[];
    disabled?: boolean;
    onApplyValues: (values: Record<string, string>) => void;
};

export function ActivityEntryPrefillControls({
    scope,
    entityId,
    reportTemplateId,
    reportTagId,
    targetSince,
    kpiContexts,
    disabled = false,
    onApplyValues,
}: ActivityEntryPrefillControlsProps) {
    const [candidates, setCandidates] = useState<ActivityEntryPrefillCandidate[]>([]);
    const [available, setAvailable] = useState(false);
    const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
    const [loadingCandidates, setLoadingCandidates] = useState(false);
    const [applying, setApplying] = useState(false);
    const [appliedLabel, setAppliedLabel] = useState<string | null>(null);

    const loadKey = `${scope}-${entityId}-${reportTemplateId}-${reportTagId}`;

    useEffect(() => {
        let cancelled = false;
        setCandidates([]);
        setAvailable(false);
        setSelectedTagId(null);
        setAppliedLabel(null);

        if (entityId <= 0 || reportTemplateId <= 0 || reportTagId <= 0) {
            return;
        }

        setLoadingCandidates(true);
        void (async () => {
            const response = await fetchActivityEntryPrefillAsync({
                scope,
                entityId,
                reportTemplateId,
                reportTagId,
            });
            if (cancelled) return;
            setLoadingCandidates(false);

            if (!response.isSuccess()) {
                setAvailable(false);
                setCandidates([]);
                return;
            }

            const data = response.getData() as {
                available?: boolean;
                candidates?: ActivityEntryPrefillCandidate[];
            } | null;
            const nextAvailable = Boolean(data?.available);
            const nextCandidates = Array.isArray(data?.candidates) ? data!.candidates : [];
            setAvailable(nextAvailable);
            setCandidates(nextCandidates);
            setSelectedTagId(pickDefaultPrefillCandidate(nextCandidates, targetSince));
        })();

        return () => {
            cancelled = true;
        };
    }, [loadKey, scope, entityId, reportTemplateId, reportTagId, targetSince]);

    const selectedCandidate = useMemo(
        () => candidates.find((c) => c.report_tag_id === selectedTagId) ?? null,
        [candidates, selectedTagId]
    );

    const handleApply = useCallback(async () => {
        if (!selectedTagId || applying || disabled) return;
        setApplying(true);
        try {
            const response = await fetchActivityEntryPrefillAsync({
                scope,
                entityId,
                reportTemplateId,
                reportTagId,
                sourceReportTagId: selectedTagId,
            });
            if (!response.isSuccess()) {
                const detail =
                    response.getError()?.detail ||
                    "Prefill could not be applied. Try again or save your draft first.";
                showError(detail);
                return;
            }

            const data = response.getData() as ActivityEntryPrefillValuesResponse | null;
            const entries = Array.isArray(data?.entries) ? data!.entries : [];
            const mapped = mapPrefillEntriesToFormValues(entries, kpiContexts);
            onApplyValues(mapped);

            const label =
                data?.source_report_tag?.name ??
                selectedCandidate?.name ??
                "previous period";
            setAppliedLabel(label);
        } finally {
            setApplying(false);
        }
    }, [
        selectedTagId,
        applying,
        disabled,
        scope,
        entityId,
        reportTemplateId,
        reportTagId,
        kpiContexts,
        onApplyValues,
        selectedCandidate?.name,
    ]);

    if (!available || (!loadingCandidates && candidates.length === 0)) {
        return null;
    }

    return (
        <div className="d-flex flex-column align-items-stretch align-items-md-end gap-1">
            <div className="d-flex flex-wrap justify-content-md-end align-items-center gap-2">
                <Form.Label className="mb-0 small text-muted text-nowrap">
                    Prefill from
                </Form.Label>
                <Form.Select
                    size="sm"
                    className="activity-entry-prefill__select"
                    style={{ minWidth: "14rem", maxWidth: "22rem" }}
                    value={selectedTagId ?? ""}
                    disabled={disabled || loadingCandidates || applying || candidates.length === 0}
                    onChange={(e) => {
                        const next = Number(e.target.value);
                        setSelectedTagId(Number.isFinite(next) && next > 0 ? next : null);
                    }}
                    aria-label="Prefill from reporting period"
                >
                    {loadingCandidates ? (
                        <option value="">Loading…</option>
                    ) : (
                        candidates.map((candidate) => (
                            <option
                                key={candidate.report_tag_id}
                                value={candidate.report_tag_id}
                            >
                                {formatPrefillCandidateLabel(candidate)}
                            </option>
                        ))
                    )}
                </Form.Select>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    className="btn-action"
                    onClick={() => {
                        void handleApply();
                    }}
                    disabled={
                        disabled ||
                        loadingCandidates ||
                        applying ||
                        !selectedTagId ||
                        candidates.length === 0
                    }
                    aria-busy={applying}
                >
                    {applying ? (
                        <>
                            <Spinner
                                animation="border"
                                size="sm"
                                className="me-1"
                                aria-hidden
                            />
                            Apply
                        </>
                    ) : (
                        "Apply"
                    )}
                </Button>
            </div>
            {appliedLabel ? (
                <div className="small text-muted">
                    Values from {appliedLabel} · not saved yet
                </div>
            ) : null}
        </div>
    );
}
